import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Heart, Bed, Bath, Maximize, MapPin, ShieldCheck, Crown, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1704457030386-1663afc19e3d?w=600&q=80',
  'https://images.pexels.com/photos/7031604/pexels-photo-7031604.jpeg?auto=compress&w=600',
  'https://images.pexels.com/photos/8134745/pexels-photo-8134745.jpeg?auto=compress&w=600',
  'https://images.pexels.com/photos/3797991/pexels-photo-3797991.jpeg?auto=compress&w=600',
];

export function getImageUrl(path, index = 0) {
  if (!path) return PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
  if (path.startsWith('http')) return path;
  return `${API}/files/${path}`;
}

export function formatPrice(price, currency = 'USD', operation = 'sale') {
  const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);
  return operation === 'rent' ? `${formatted}/mo` : formatted;
}

const TRUST_PILL_STYLE = {
  platinum: 'bg-gradient-to-br from-[#D4AF37] to-[#C8A028] text-[#0B192C]',
  gold: 'bg-[#D4AF37] text-[#0B192C]',
  silver: 'bg-slate-200 text-[#0B192C]',
  building: 'bg-white/95 text-slate-600',
};
const TRUST_PILL_ICON = { platinum: Crown, gold: ShieldCheck, silver: ShieldCheck, building: Sparkles };

export default function PropertyCard({ property, index = 0, onFavoriteToggle }) {
  const { t } = useLanguage();
  const { user, token, isAuthenticated } = useAuth();
  const [isFav, setIsFav] = useState(user?.favorites?.includes(property.id) || false);
  const [imgError, setImgError] = useState(false);

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) { toast.error('Please sign in to save favorites'); return; }
    try {
      if (isFav) {
        await axios.delete(`${API}/users/favorites/${property.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setIsFav(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/users/favorites/${property.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsFav(true);
        toast.success('Added to favorites');
      }
      onFavoriteToggle?.();
    } catch { toast.error('Failed to update favorites'); }
  };

  const imageUrl = imgError ? PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length] : getImageUrl(property.main_photo, index);

  return (
    <Link to={`/property/${property.id}`} data-testid={`property-card-${property.id}`}
      className="group relative bg-white rounded-xl overflow-hidden border border-border/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 block">
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        <img src={imageUrl} alt={property.title} loading="lazy"
          onError={() => setImgError(true)}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap max-w-[80%]">
          <Badge className="bg-primary text-white text-xs font-semibold px-2 py-0.5 rounded-full">{t(`types.${property.property_type}`)}</Badge>
          <Badge className={`text-white text-xs font-semibold px-2 py-0.5 rounded-full ${property.operation_type === 'rent' ? 'bg-teal-600' : 'bg-[#D4AF37]'}`}>
            {t(`operations.${property.operation_type}`)}
          </Badge>
          {property.property_verified && (
            <Badge
              className="bg-emerald-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
              title="WakaCasa Verified Property"
              data-testid={`prop-verified-badge-${property.id}`}
            >
              <ShieldCheck className="w-3 h-3" />Verified
            </Badge>
          )}
          {!property.property_verified && property.verification_status === 'paid_pending_review' && (
            <Badge
              className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 border border-amber-300"
              title="Verification under review by WakaCasa"
              data-testid={`prop-pending-badge-${property.id}`}
            >
              <ShieldCheck className="w-3 h-3" />Pending
            </Badge>
          )}
        </div>
        {/* Favorite */}
        <button onClick={toggleFavorite} data-testid={`favorite-btn-${property.id}`}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-sm">
          <Heart className={`w-4 h-4 ${isFav ? 'fill-red-500 text-red-500' : 'text-slate-500'}`} />
        </button>
        {/* Price overlay */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
          <span className="bg-[#0B192C]/90 backdrop-blur-sm text-[#D4AF37] font-heading font-bold text-lg px-3 py-1 rounded-full shadow-md">
            {formatPrice(property.price, property.currency, property.operation_type)}
          </span>
          {property.owner_trust && (() => {
            const ot = property.owner_trust;
            const TIcon = TRUST_PILL_ICON[ot.tier] || Sparkles;
            return (
              <span
                className={`inline-flex items-center gap-1 backdrop-blur-sm shadow-md text-xs font-semibold px-2 py-1 rounded-full ${TRUST_PILL_STYLE[ot.tier]}`}
                title={`Trust Score ${ot.score}/100 · ${ot.tier_label}`}
                data-testid={`card-trust-pill-${property.id}`}
              >
                <TIcon className="w-3 h-3" />
                {ot.score}
              </span>
            );
          })()}
        </div>
      </div>
      {/* Content */}
      <div className="p-4">
        <h3 className="font-heading font-semibold text-base text-slate-900 truncate group-hover:text-primary transition-colors">{property.title}</h3>
        <div className="flex items-center gap-1 mt-1 text-slate-500">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-sm truncate">{property.city}{property.neighborhood ? `, ${property.neighborhood}` : ''}</span>
        </div>
        {property.property_type !== 'land' && (
          <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
            {property.bedrooms > 0 && <div className="flex items-center gap-1"><Bed className="w-3.5 h-3.5 text-slate-400" /><span>{property.bedrooms}</span></div>}
            {property.bathrooms > 0 && <div className="flex items-center gap-1"><Bath className="w-3.5 h-3.5 text-slate-400" /><span>{property.bathrooms}</span></div>}
            <div className="flex items-center gap-1"><Maximize className="w-3.5 h-3.5 text-slate-400" /><span>{property.area} m&sup2;</span></div>
          </div>
        )}
        {property.property_type === 'land' && (
          <div className="flex items-center gap-1 mt-3 text-sm text-slate-600">
            <Maximize className="w-3.5 h-3.5 text-slate-400" /><span>{property.area} m&sup2;</span>
          </div>
        )}
      </div>
    </Link>
  );
}
